/**
 * run-checkbox — Default skin module
 *
 * Exports { styles, render } per SkinModule contract.
 * render(host) writes shadow DOM to host.shadowRoot.
 * styles is a CSS string; run-checkbox.js creates the CSSStyleSheet from it.
 *
 * Reads host properties read-only. Never writes host state.
 * Token disposition: pass-through (no overrides)
 */

export const styles = `
  *, *::before, *::after { box-sizing: border-box; }

  :host {
    display: inline-flex;
    align-items: flex-start;
    cursor: pointer;
  }

  :host([disabled]) {
    cursor: not-allowed;
    opacity: var(--run-checkbox-disabled-opacity, 0.4);
    pointer-events: none;
  }

  :host([readonly]) {
    cursor: not-allowed;
  }

  [part="base"] {
    display: inline-flex;
    align-items: flex-start;
    gap: var(--run-checkbox-label-gap, 8px);
    min-height: 44px;
    padding: 0;
  }

  :host([reversed]) [part="base"] {
    flex-direction: row-reverse;
  }

  [part="control"] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: var(--run-checkbox-control-size, 20px);
    height: var(--run-checkbox-control-size, 20px);
    margin-top: 2px;
    background: var(--run-checkbox-control-bg, var(--run-color-surface-default, #fff));
    border: var(--run-checkbox-control-border-width, 2px) solid var(--run-checkbox-control-border-color, var(--run-color-border-default, #94a3b8));
    border-radius: var(--run-checkbox-control-border-radius, var(--run-radius-semantic-control, 4px));
    transition: background var(--run-checkbox-transition-duration, 0.15s) ease,
                border-color var(--run-checkbox-transition-duration, 0.15s) ease;
  }

  :host([checked]) [part="control"],
  :host([indeterminate]) [part="control"] {
    background: var(--run-checkbox-control-bg-checked, var(--run-color-intent-primary-base, #2563eb));
    border-color: var(--run-checkbox-control-border-color-checked, var(--run-color-intent-primary-base, #2563eb));
  }

  :host(:hover:not([disabled]):not([readonly])) [part="control"] {
    border-color: var(--run-checkbox-control-border-color-hover, var(--run-color-border-strong, #64748b));
  }

  :host([checked]:hover:not([disabled]):not([readonly])) [part="control"],
  :host([indeterminate]:hover:not([disabled]):not([readonly])) [part="control"] {
    background: var(--run-checkbox-control-bg-checked-hover, var(--run-color-intent-primary-hover, #1d4ed8));
    border-color: var(--run-checkbox-control-bg-checked-hover, var(--run-color-intent-primary-hover, #1d4ed8));
  }

  :host([invalid]) [part="control"] {
    border-color: var(--run-checkbox-control-border-color-invalid, var(--run-color-intent-danger-base, #dc2626));
  }

  :host(:focus-visible) [part="control"] {
    outline: var(--run-checkbox-focus-ring-width, 2px) solid var(--run-checkbox-focus-ring-color, var(--run-color-focus-ring, #2563eb));
    outline-offset: var(--run-checkbox-focus-ring-offset, 2px);
  }

  [part="indicator"] {
    display: block;
    color: var(--run-checkbox-indicator-color, var(--run-color-intent-primary-on, #fff));
    width: 12px;
    height: 12px;
  }

  [part="label-group"] {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  [part="label"] {
    color: var(--run-checkbox-label-color, var(--run-color-text-primary, #1e293b));
    font-size: var(--run-checkbox-label-font-size, var(--run-font-size-base, 14px));
    line-height: var(--run-checkbox-label-line-height, 1.5);
    user-select: none;
  }

  :host([disabled]) [part="label"] {
    color: var(--run-checkbox-label-color-disabled, var(--run-color-text-disabled, #94a3b8));
  }

  [part="help-text"] {
    color: var(--run-checkbox-help-text-color, var(--run-color-text-tertiary, #64748b));
    font-size: var(--run-checkbox-help-text-font-size, var(--run-font-size-sm, 12px));
    line-height: 1.4;
  }

  :host([invalid]) [part="help-text"] {
    color: var(--run-checkbox-help-text-color-invalid, var(--run-color-text-danger, #dc2626));
  }

  /* Size variants */
  :host([size="sm"]) [part="control"] {
    width: var(--run-checkbox-control-size-sm, 16px);
    height: var(--run-checkbox-control-size-sm, 16px);
  }
  :host([size="sm"]) [part="indicator"] { width: 10px; height: 10px; }
  :host([size="sm"]) [part="label"] { font-size: var(--run-font-size-sm, 12px); }

  :host([size="lg"]) [part="control"] {
    width: var(--run-checkbox-control-size-lg, 24px);
    height: var(--run-checkbox-control-size-lg, 24px);
  }
  :host([size="lg"]) [part="indicator"] { width: 14px; height: 14px; }
  :host([size="lg"]) [part="label"] { font-size: var(--run-font-size-lg, 16px); }

  @media (prefers-reduced-motion: reduce) {
    [part="control"] { transition: none; }
  }
`;

/**
 * Render shadow DOM for run-checkbox.
 * Writes directly to host.shadowRoot.innerHTML.
 *
 * @param {HTMLElement} host - The run-checkbox instance
 */
export function render(host) {
  // Always render the indicator SVG — content changes with state
  const indicatorInner = host.indeterminate
    ? '<line x1="2" y1="6" x2="10" y2="6" stroke-linecap="round"/>'
    : host.checked
      ? '<polyline points="2,6 5,9 10,3" stroke-linecap="round" stroke-linejoin="round"/>'
      : '';

  const indicatorSVG = `<svg part="indicator" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">${indicatorInner}</svg>`;

  // Label: property takes precedence over slot content
  const labelContent = host.label != null
    ? `<span>${host.label}</span>`
    : `<slot></slot>`;

  host.shadowRoot.innerHTML = `
    <label part="base">
      <span part="control">${indicatorSVG}</span>
      <span part="label-group">
        <span part="label">${labelContent}</span>
        <span part="help-text"><slot name="help-text"></slot></span>
      </span>
    </label>
  `;
}
